import { UnitSystem } from 'src/app/shared/enums/unit-system.enum';
import { REGEXP_PATTERNS } from 'src/app/shared/consts/regexp-patterns';

export const GEOTHERMAL_MAX_MIN_VALIDATORS = {
    POISSON_MIN: 0,
    POISSON_MAX: 0.5,
    FRICTION_MIN: 0,
    FRICTION_MAX: 1,
    MODULUS_E_MIN: { USC: 2.5e7, SI: 172369 },
    MODULUS_E_MAX: { USC: 3.5e7, SI: 241317 },
    THERMAL_EXP_MIN: { USC: 0, SI: 0 },
    THERMAL_EXP_MAX: { USC: 1e-4, SI: 0.00018 },
    OUTSIDE_DIAMETER_MIN: { USC: 1, SI: 25.4 },
    OUTSIDE_DIAMETER_MAX: { USC: 27, SI: 685.8 },
    NOMINAL_WEIGHT_MIN: { USC: 4, SI: 5.95 },
    NOMINAL_WEIGHT_MAX: { USC: 400, SI: 595.27 },
    INSIDE_DIAMETER_MIN: { USC: 0, SI: 0 },
    INSIDE_DIAMETER_MAX: { USC: 27, SI: 685.8 },
    SMYS_MIN: { USC: 20000, SI: 138 },
    SMYS_MAX: { USC: 170000, SI: 1172 },
    TEMP_DERATING_MIN: { USC: 0, SI: 0 },
    TEMP_DERATING_MAX: { USC: 0.1, SI: 0.1 },
    HOLLIMIT_MIN: { USC: 1, SI: 1 },
    HOLLIMIT_MAX: { USC: 2, SI: 2 },
    HOLE_ID_MIN: { USC: 1, SI: 25.4 },
    HOLE_ID_MAX: { USC: 36, SI: 914.4 },
    DENSITY_MIN: { USC: 0, SI: 0 },
    DENSITY_MAX: { USC: 25, SI: 2996 },
    PRESSURE_MIN: { USC: 0, SI: 0 },
    PRESSURE_MAX_IN: { USC: 40000, SI: 275.79 },
    PRESSURE_MAX_OUT: { USC: 400000, SI: 2757.9 },
    TEMPERATURE_MIN: { USC: 0, SI: -17.778 },
    TEMPERATURE_MAX: { USC: 1200, SI: 648.89 },
    DELTA_L_PRELOAD_MIN: { USC: -500, SI: -152.4 },
    DELTA_L_PRELOAD_MAX: { USC: 500, SI: 152.4 },
};

export const GEOTHERMAL_DECIMAL_PLACES_VALIDATORS = {
    modulusE: { USC: 0, SI: 0 },
    poisson: { USC: 2, SI: 2 },
    thermalExp: { USC: 7, SI: 7 },
    friction: { USC: 2, SI: 2 },
    eccentricity: { USC: 3, SI: 2 },
    outsideDiameter: { USC: 3, SI: 2 },
    nominalWeight: { USC: 2, SI: 2 },
    wallThickness: { USC: 3, SI: 2 },
    insideDiameter: { USC: 3, SI: 2 },
    smys: { USC: 0, SI: 0 },
    tempDerating: { USC: 3, SI: 3 },
    hollLimit: { USC: 2, SI: 2 },
    holeId: { USC: 3, SI: 2 },
    insideDensityInitial: { USC: 3, SI: 0 },
    insideDensityFinal: { USC: 3, SI: 0 },
    outsideDensityInitial: { USC: 3, SI: 0 },
    outsideDensityFinal: { USC: 3, SI: 0 },
    insidePressureInitial: { USC: 1, SI: 2 },
    insidePressureFinal: { USC: 1, SI: 2 },
    outsidePressureInitial: { USC: 1, SI: 2 },
    outsidePressureFinal: { USC: 1, SI: 2 },
    avgTemperatureInitial: { USC: 3, SI: 3 },
    avgTemperatureFinal: { USC: 3, SI: 3 },
    tempT: { USC: 1, SI: 1 },
    lusCoeffL1: { USC: 4, SI: 4 },
    lusCoeffL2: { USC: 4, SI: 4 },
    lusCoeffL3: { USC: 4, SI: 4 },
    lusCoeffL4: { USC: 4, SI: 4 },
    lusCoeffL5: { USC: 4, SI: 4 },
    lusCoeffL6: { USC: 4, SI: 4 },
    lusCoeffL7: { USC: 4, SI: 4 },
    lusCoeffL8: { USC: 4, SI: 4 },
    lusCoeffL9: { USC: 4, SI: 4 },
    lusCoeffL10: { USC: 4, SI: 4 },
    lusCoeffL11: { USC: 4, SI: 4 },
    lusCoeffL12: { USC: 4, SI: 4 },
    deltaLPreload: { USC: 4, SI: 4 },
    tempT1: { USC: 1, SI: 1 },
    tempT2: { USC: 1, SI: 1 },
    tempT3: { USC: 1, SI: 1 },
    tempT4: { USC: 1, SI: 1 },
    tempT5: { USC: 1, SI: 1 },
};

export function getMaxMinValidator(key: string, unitSystem?: UnitSystem): number {
    if (!unitSystem) {
        return GEOTHERMAL_MAX_MIN_VALIDATORS[key];
    }
    const entry = GEOTHERMAL_MAX_MIN_VALIDATORS[key];
    if (entry && typeof entry === 'object' && unitSystem in entry) {
        return entry[unitSystem];
    }
    return undefined;
}

export function getDecimalPattern(field: string, unitSystem: UnitSystem.USC | UnitSystem.SI = UnitSystem.USC): string {
    let decimals = 2;
    if (GEOTHERMAL_DECIMAL_PLACES_VALIDATORS[field]) {
        decimals = GEOTHERMAL_DECIMAL_PLACES_VALIDATORS[field][unitSystem] ?? 2;
    }

    // Ahora todos los campos permiten hasta N decimales (no exactamente N)
    switch (decimals) {
        case 0:
            return REGEXP_PATTERNS.SIGNED_NUMBER_0_DECIMALS;
        case 1:
            return REGEXP_PATTERNS.SIGNED_NUMBER_1_DECIMALS;
        case 2:
            return REGEXP_PATTERNS.SIGNED_NUMBER_2_DECIMALS;
        case 3:
            return REGEXP_PATTERNS.SIGNED_NUMBER_3_DECIMALS;
        case 4:
            return REGEXP_PATTERNS.SIGNED_NUMBER_4_DECIMALS;
        case 7:
            return REGEXP_PATTERNS.SIGNED_NUMBER_7_DECIMALS;
        default:
            return REGEXP_PATTERNS.SIGNED_NUMBER_2_DECIMALS;
    }
}

